import React from 'react'
import styled from '@emotion/styled'
import { FaCheckCircle } from 'react-icons/fa'
import { FaCircleXmark } from 'react-icons/fa6'
import { GOScheduleMenu } from '@types'
import { colors } from '../../../../constants'
import {
  formatCutoffTime,
  formatNumberToTime,
} from './MenusFinder/GroupOrderMenusFinder'
import { Panel, YSpacing } from '@components/common'

interface MenuDetailsProps {
  menu: GOScheduleMenu
}

const MenuDetails: React.FC<MenuDetailsProps> = ({ menu }) => {
  // Helper function to render boolean values with icons
  const renderBooleanValue = (value: boolean) => {
    return value ? (
      <StatusIcon color="green">
        <FaCheckCircle size={16} />
      </StatusIcon>
    ) : (
      <StatusIcon color="red">
        <FaCircleXmark size={16} />
      </StatusIcon>
    )
  }

  // Helper function to render menu items in a 3-column grid
  const renderMenuItems = (items: any[] = []) => {
    if (!items.length) {
      return <div>No menu items available</div>
    }

    return (
      <MenuItemsGrid>
        {items.map((item, index) => (
          <MenuItem key={index}>{item.name}</MenuItem>
        ))}
      </MenuItemsGrid>
    )
  }

  return (
    <DetailsContainer>
      <Panel>
        <PanelInner>
          <DetailsHeader>{menu.name}</DetailsHeader>
          <YSpacing height="20px" />

          <DetailsGrid>
            {/* Column 1 */}
            <DetailsColumn>
              <DetailItem>
                <DetailLabel>Menu ID</DetailLabel>
                <DetailValue>{menu.id}</DetailValue>
              </DetailItem>

              <DetailItem>
                <DetailLabel>Cuisine Tags</DetailLabel>
                <DetailValue>{menu.cuisineTags.join(', ')}</DetailValue>
              </DetailItem>

              <DetailItem>
                <DetailLabel>Ready to Heat</DetailLabel>
                <DetailValue>
                  {renderBooleanValue(menu.readyToHeat)}
                </DetailValue>
              </DetailItem>

              <DetailItem>
                <DetailLabel>Avg Menu Item Price</DetailLabel>
                <DetailValue>${menu.avgPrice}</DetailValue>
              </DetailItem>

              <DetailItem>
                <DetailLabel>Avg Menu Margin</DetailLabel>
                <DetailValue>{menu.margin.toFixed(2)}%</DetailValue>
              </DetailItem>

              <DetailItem>
                <DetailLabel>Market Type</DetailLabel>
                <DetailValue>{menu.marketType}</DetailValue>
              </DetailItem>
            </DetailsColumn>

            {/* Column 2 */}
            <DetailsColumn>
              <DetailItem>
                <DetailLabel>Vendor</DetailLabel>
                <DetailValue>{menu.chef.name}</DetailValue>
              </DetailItem>

              <DetailItem>
                <DetailLabel>Menu Card Cutoff Time</DetailLabel>
                <DetailValue>
                  {formatCutoffTime(
                    menu.groupOrderCutoffDays,
                    menu.groupOrderCutoffTime,
                  )}
                </DetailValue>
              </DetailItem>

              <DetailItem>
                <DetailLabel>Pickup Time (On Date)</DetailLabel>
                <DetailValue>{menu.pickupTime}</DetailValue>
              </DetailItem>

              <DetailItem>
                <DetailLabel>Earliest Pickup Time</DetailLabel>
                <DetailValue>
                  {menu.groupOrderEarliestPickupTime &&
                  menu.groupOrderEarliestPickupTime >= 100
                    ? formatNumberToTime(menu.groupOrderEarliestPickupTime)
                    : 'N/A'}
                </DetailValue>
              </DetailItem>

              <DetailItem>
                <DetailLabel>Vendor Max Items / Day</DetailLabel>
                <DetailValue>{menu.vendorMaxGOItems}</DetailValue>
              </DetailItem>

              <DetailItem>
                <DetailLabel>Menu Ordering Cap</DetailLabel>
                <DetailValue>{menu.menuMaxItems}</DetailValue>
              </DetailItem>
            </DetailsColumn>

            {/* Column 3 */}
            <DetailsColumn>
              <DetailItem>
                <DetailLabel>Client Name</DetailLabel>
                <DetailValue>{menu.clientName}</DetailValue>
              </DetailItem>

              <DetailItem>
                <DetailLabel>Last Ordered by Client</DetailLabel>
                <DetailValue>{menu.lastOrdered || 'Never'}</DetailValue>
              </DetailItem>

              <DetailItem>
                <DetailLabel>Distance from Client</DetailLabel>
                <DetailValue>
                  {menu.distance !== '' ? `${menu.distance} miles` : 'N/A'}
                </DetailValue>
              </DetailItem>

              <DetailItem>
                <DetailLabel>Avg Client Menu Item Rating</DetailLabel>
                <DetailValue>
                  {menu.avgMenuItemRatingByClient || 'N/A'}
                </DetailValue>
              </DetailItem>

              <DetailItem>
                <DetailLabel>Cuisines ID</DetailLabel>
                <DetailValue>{menu.cuisinesId}</DetailValue>
              </DetailItem>

              <DetailItem>
                <DetailLabel>Cutoff Time</DetailLabel>
                <DetailValue>{menu.cutoffTime}</DetailValue>
              </DetailItem>
            </DetailsColumn>
          </DetailsGrid>

          {/* Menu Items Section */}
          <YSpacing height="20px" />
          <MenuItemsSection>
            <SectionHeader>Menu Items</SectionHeader>
            <YSpacing height="10px" />
            <MenuItemsContent>
              {renderMenuItems(menu.allMenuItems)}
            </MenuItemsContent>
          </MenuItemsSection>
        </PanelInner>
      </Panel>
    </DetailsContainer>
  )
}

const DetailsContainer = styled.div`
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
`

const PanelInner = styled.div`
  padding: 20px;
`

const DetailsHeader = styled.h2`
  font-size: 24px;
  font-family: 'bold';
  color: ${colors.gray400};
  border-bottom: 2px solid ${colors.gray200};
  padding-bottom: 10px;
`

const DetailsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 25px;

  @media (max-width: 992px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`

const DetailsColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
`

const DetailItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
`

const DetailLabel = styled.div`
  font-family: 'bold';
  font-size: 13px;
  color: ${colors.gray400};
  text-transform: uppercase;
`

const DetailValue = styled.div`
  font-size: 15px;
  color: ${colors.dark500};
  word-break: break-word;
  display: flex;
  align-items: center;
`

const StatusIcon = styled.div<{ color: string }>`
  color: ${(props) =>
    props.color === 'green' ? colors.green : colors.dark500};
  display: flex;
  align-items: center;
`

const SectionHeader = styled.h3`
  font-family: 'bold';
  font-size: 16px;
  color: ${colors.gray400};
  padding-bottom: 5px;
  border-bottom: 1px solid ${colors.gray200};
`

const MenuItemsSection = styled.div`
  margin-top: 10px;
  background-color: ${colors.gray100};
  border-radius: 8px;
  padding: 15px;
  border: 1px solid ${colors.gray200};
`

const MenuItemsContent = styled.div`
  font-size: 14px;
  color: ${colors.gray400};
  line-height: 1.5;
`

const MenuItemsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;

  @media (max-width: 992px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`

const MenuItem = styled.div`
  background-color: white;
  border-radius: 6px;
  padding: 8px 12px;
  font-size: 14px;
  color: ${colors.gray400};
  border: 1px solid ${colors.gray200};
  transition: all 0.2s ease;

  &:hover {
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
    border-color: ${colors.gray300};
  }
`

export default MenuDetails
